import React, { useState } from 'react';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export default function MissionClosureDialog({ mission, open, onClose, user }) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState('');
  const [evidence, setEvidence] = useState('');
  const [learnings, setLearnings] = useState('');
  const [learningAction, setLearningAction] = useState('');

  const completeMissionMutation = useMutation({
    mutationFn: async (data) => {
      // Update mission
      await base44.entities.Mission.update(mission.id, {
        status: 'completed',
        outcome: data.outcome,
        evidence: data.evidence,
        learnings: data.learnings,
        completed_at: new Date().toISOString(),
        completed_by: user?.email
      });

      // Create policy or capability if learnings exist
      if (data.learnings && data.learningAction === 'policy') {
        await base44.entities.Policy.create({
          title: `Learning from ${mission.title}`,
          statement: data.learnings,
          rationale: `Derived from mission completion: ${mission.mission_code}`,
          designed_by: user?.email,
          designed_at: new Date().toISOString(),
          status: 'draft'
        });
      } else if (data.learnings && data.learningAction === 'capability') {
        await base44.entities.Capability.create({
          name: `Capability from ${mission.title}`,
          description: data.learnings,
          type: 'developed',
          status: 'requested',
          requested_by: user?.email,
          requested_at: new Date().toISOString()
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] });
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      onClose();
      setOutcome('');
      setEvidence('');
      setLearnings('');
      setLearningAction('');
    }
  });

  const handleSubmit = () => {
    if (!outcome || !evidence) {
      alert('Outcome and Evidence are required');
      return;
    }
    
    completeMissionMutation.mutate({
      outcome,
      evidence,
      learnings,
      learningAction
    });
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="missions-missionclosuredialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-400">
            <CheckCircle className="w-5 h-5" />
            Mission Closure: {mission?.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
            <p className="text-sm text-slate-300">
              Document the results of this mission. This information will be archived and used for future reference.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Outcome *</Label>
            <Textarea data-testid="missions-missionclosuredialog-textarea"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="What was achieved? What were the results of this mission?"
              className="bg-slate-950 border-slate-700 min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Evidence *</Label>
            <Textarea data-testid="missions-missionclosuredialog-textarea-1"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder="What proof do you have? Links, documents, metrics, etc."
              className="bg-slate-950 border-slate-700 min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Learnings</Label>
            <Textarea data-testid="missions-missionclosuredialog-textarea-2"
              value={learnings}
              onChange={(e) => setLearnings(e.target.value)}
              placeholder="What did you learn? What should be done differently next time?"
              className="bg-slate-950 border-slate-700 min-h-[100px]"
            />
          </div>

          {learnings && (
            <div className="space-y-2">
              <Label className="text-slate-300">Convert Learnings To:</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={learningAction === 'policy' ? 'default' : 'outline'}
                  onClick={() => setLearningAction(learningAction === 'policy' ? '' : 'policy')}
                  className={learningAction === 'policy' ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-700 text-slate-400'}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  New Policy
                </Button>
                <Button
                  type="button"
                  variant={learningAction === 'capability' ? 'default' : 'outline'}
                  onClick={() => setLearningAction(learningAction === 'capability' ? '' : 'capability')}
                  className={learningAction === 'capability' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-slate-700 text-slate-400'}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Requested Capability
                </Button>
              </div>
              {learningAction && (
                <div className="p-3 bg-slate-950 border border-slate-700 rounded text-xs text-slate-400">
                  A new {learningAction === 'policy' ? 'Policy' : 'Capability Request'} will be created in the Issues, Policies & Capabilities page.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
            <Button data-testid="missions-cancel-btn" variant="outline" onClick={onClose} className="border-slate-700 text-slate-400">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={completeMissionMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {completeMissionMutation.isPending ? 'Completing...' : 'Complete Mission'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </ConfirmableDialog>
  );
}